import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/Dashboard.css";

export default function Dashboard() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle resize events
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Floating animations for background elements - optimized for mobile
  useEffect(() => {
    const createFloatingElement = () => {
      // Reduce number of elements on mobile for better performance
      if (isMobile && Math.random() > 0.7) return;

      const elements = ["🔤", "🧩", "🎈", "🔢", "⭐", "🐱", "🐶", "🌈"];
      const element = document.createElement("div");
      element.className = "floating-element";
      element.textContent =
        elements[Math.floor(Math.random() * elements.length)];
      element.style.left = `${Math.random() * 100}vw`;
      element.style.fontSize = `${Math.random() * (isMobile ? 16 : 24) + 16}px`;
      element.style.animationDuration = `${Math.random() * 10 + 10}s`;
      document.querySelector(".dashboard-background").appendChild(element);

      setTimeout(() => {
        if (element.parentNode) {
          element.remove();
        }
      }, 20000);
    };

    const interval = setInterval(createFloatingElement, isMobile ? 1500 : 1000);
    return () => clearInterval(interval);
  }, [isMobile]);

  // Sample data
  const categories = [
    {
      id: "alphabet",
      title: "Alphabet",
      icon: "🔤",
      color: "#4CAF50",
      gradient: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
      games: [
        {
          id: "alphabet-puzzle",
          title: "Alphabet Puzzle",
          description: "Put the letters in order",
          icon: "🧩",
          path: "/alphabet-puzzle",
        },
        {
          id: "balloon-pop",
          title: "Balloon Alphabet Pop",
          description: "Pop balloons with letters",
          icon: "🎈",
          path: "/balloon-letter-pop",
        },
      ],
    },
    {
      id: "numbers",
      title: "Numbers",
      icon: "🔢",
      color: "#2196F3",
      gradient: "linear-gradient(135deg, #2196F3 0%, #0D47A1 100%)",
      games: [
        {
          id: "number-match",
          title: "Number Match",
          description: "Match numbers with quantities",
          icon: "➗",
          path: "/number-match",
        },
      ],
    },

    {
      id: "animals",
      title: "Animals",
      icon: "🐶",
      color: "#FFECC0",
      gradient: "linear-gradient(135deg, #FFECC0 0%, #4E342E 100%)",
      games: [
        {
          id: "animal-sounds",
          title: "Animal Sounds",
          description: "Match animals with their sounds",
          icon: "🐱",
          path: "/animal-sounds",
        },
      ],
    },
    {
      id: "shapes",
      title: "Shapes",
      icon: "⭐",
      color: "#FF9800",
      gradient: "linear-gradient(135deg, #FF9800 0%, #EF6C00 100%)",
      games: [
        {
          id: "shape-sorter",
          title: "Shape Sorter",
          description: "Drag shapes to matching holes",
          icon: "🔺",
          path: "/shape-sorter",
        },
      ],
    },
    {
      id: "colors",
      title: "Colors",
      icon: "🌈",
      color: "#E91E63",
      gradient: "linear-gradient(135deg, #E91E63 0%, #AD1457 100%)",
      games: [
        {
          id: "color-mixer",
          title: "Color Mixer",
          description: "Mix primary colors to make new ones",
          icon: "🎨",
          path: "/color-mixer",
        },
      ],
    },

    {
      id: "words",
      title: "First Words",
      icon: "📝",
      color: "#9C27B0",
      gradient: "linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)",
      games: [
        {
          id: "word-match",
          title: "Word Match",
          description: "Match words with pictures",
          icon: "🔤",
          path: "/word-match",
        },
      ],
    },
  ];

  const handleCategorySelect = (categoryId) => {
    navigate(`/category/${categoryId}`);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 120 },
    },
  };

  return (
    <div className="dashboard-container">
      {/* Animated background elements */}
      <div className="dashboard-background"></div>

      {/* Floating shapes - hidden on mobile for better performance */}
      {!isMobile && (
        <div className="floating-shapes">
          <div className="shape circle"></div>
          <div className="shape triangle"></div>
          <div className="shape square"></div>
          <div className="shape star"></div>
        </div>
      )}

      <motion.div
        className="container-fluid py-4 px-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <header className="text-center mb-4 mb-md-5">
          <motion.h1
            className="display-4 fw-bold title-text"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 120 }}
          >
            Kids Learning Playground
          </motion.h1>
          <motion.p
            className="lead subtitle"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 120 }}
          >
            Choose a category to start learning!
          </motion.p>
        </header>

        {/* Categories Grid */}
        <motion.div
          className="categories-section"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="row g-3 g-md-4">
            {categories.map((category) => (
              <div key={category.id} className="col-6 col-md-6 col-lg-4">
                <motion.div
                  className="category-card h-100"
                  variants={itemVariants}
                  whileHover={{
                    y: isMobile ? 0 : -10,
                    scale: isMobile ? 1 : 1.02,
                    transition: { type: "spring", stiffness: 300 },
                  }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleCategorySelect(category.id)}
                  style={{
                    background: category.gradient,
                    boxShadow: `0 10px 20px ${category.color}40`,
                  }}
                >
                  <div className="category-icon-wrapper">
                    <div className="category-icon">{category.icon}</div>
                  </div>
                  <h3 className="h4 category-title">{category.title}</h3>
                  <div className="category-hover-content">
                    <p className="mb-0">
                      Explore {category.games.length} games
                    </p>
                    <div className="play-arrow">→</div>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Footer */}
        <motion.footer
          className="text-center mt-4 mt-md-5 pt-4 pt-md-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <p className="footer-text">Fun learning adventures for kids!</p>
        </motion.footer>
      </motion.div>
    </div>
  );
}
